import { Component, PLATFORM_ID, Inject, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { HomeGraficoPercentualResponse } from '../../interfaces/home-grafico-percentual.interface';

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './donut-chart.html',
  styleUrls: ['./donut-chart.scss']
})
export class DonutChart implements OnChanges {
  @Input() data: HomeGraficoPercentualResponse | null = null;
  @Input() loading: boolean = false;

  chartOption: EChartsOption;
  isBrowser: boolean;
  showChart = true;
  private currentIsSmallScreen = false;
  private chartInstance: any;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentIsSmallScreen = this.getIsSmallScreen();
    this.chartOption = this.getChartOptions(this.currentIsSmallScreen, null);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.isBrowser) {
      this.chartOption = this.getChartOptions(this.currentIsSmallScreen, this.data);
      if (this.chartInstance) {
        setTimeout(() => {
          this.chartInstance.setOption(this.chartOption);
        }, 100);
      }
    }
  }

  onChartInit(ec: any) {
    this.chartInstance = ec;
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      const newIsSmallScreen = this.getIsSmallScreen();

      if (newIsSmallScreen !== this.currentIsSmallScreen) {
        this.currentIsSmallScreen = newIsSmallScreen;

        this.showChart = false;
        setTimeout(() => {
          this.chartOption = this.getChartOptions(this.currentIsSmallScreen, this.data);
          this.showChart = true;

          setTimeout(() => {
            if (this.chartInstance) {
              this.chartInstance.resize();
            }
          }, 100);
        }, 50);
      } else {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      }
    }
  }

  private getIsSmallScreen(): boolean {
    return this.isBrowser && window.innerWidth <= 1600;
  }

private getChartOptions(isSmallScreen: boolean, apiData: HomeGraficoPercentualResponse | null): EChartsOption {
  // Processar dados da API
  const trabalhados = apiData?.trabalhados ?? 0;
  const pendentes = apiData?.pendentesNovos ?? 0;

  // Calcular totais e percentuais
  const total = trabalhados + pendentes;
  const trabalhadosPercentual = total > 0 ? (trabalhados / total) * 100 : 0;
  const pendentesPercentual = total > 0 ? (pendentes / total) * 100 : 0;

  return {
    title: {
      text: 'Status de Leads',
      left: 'start',
      top: 0,
      textStyle: {
        color: '#001E50',
        fontWeight: 'bold',
        fontSize: isSmallScreen ? 16 : 20
      },
      padding: [0, 0, 50, 0]
    },

    // Duas legendas separadas
    legend: [
      {
        // Legenda ESQUERDA - Leads Trabalhados
        orient: 'vertical',
        left: '-1%',
        top: 'center',
        icon: 'none',
        itemWidth: 0,
        itemHeight: 0,
        itemGap: 15,
        textStyle: {
          fontSize: isSmallScreen ? 12 : 14,
          color: '#57737d',
          fontWeight: 'bold',
          rich: {
            // Define estilos ricos para formatação personalizada
            percent: {
              fontSize: isSmallScreen ? 20 : 24, // Fonte maior para o percentual
              fontWeight: 'bold',
              color: '#57737d',
              lineHeight: 30
            },
            label: {
              fontSize: isSmallScreen ? 12 : 14,
              color: '#57737d',
              fontWeight: 'bold',
              lineHeight: 20
            }
          }
        },
        formatter: (name: string) => {
          // Quebra "Leads Pendentes" em duas linhas
          const label = 'Leads\nPendentes';
          return `{percent|${Math.round(pendentesPercentual)}%}\n{label|${label}}`;
        },
        data: [{
          name: 'Leads Pendentes',
          icon: 'none',
          itemStyle: { color: '#57737d' }
        }],
        selectedMode: false
      },
      {
        // Legenda DIREITA - Leads Não Trabalhados
        orient: 'vertical',
        right: '-1%',
        top: 'center',
        icon: 'none',
        itemWidth: 0,
        itemHeight: 0,
        itemGap: 15,
        textStyle: {
          fontSize: isSmallScreen ? 12 : 14,
          color: '#001E50',
          fontWeight: 'bold',
          rich: {
            percent: {
              fontSize: isSmallScreen ? 20 : 24, // Fonte maior para o percentual
              fontWeight: 'bold',
              color: '#001E50',
              lineHeight: 30
            },
            label: {
              fontSize: isSmallScreen ? 12 : 14,
              color: '#001E50',
              fontWeight: 'bold',
              lineHeight: 20
            }
          }
        },
        formatter: (name: string) => {
          // Quebra "Leads Não Trabalhados" em duas linhas
          const label = 'Leads \nTrabalhados';
          return `{percent|${Math.round(trabalhadosPercentual)}%}\n{label|${label}}`;
        },
        data: [{
          name: 'Leads Trabalhados',
          icon: 'none',
          itemStyle: { color: '#001E50' }
        }],
        selectedMode: false
      }
    ],

    series: [
      {
        name: 'Leads',
        type: 'pie',
        radius: isSmallScreen ? '30%' : '40%',
        center: ['48%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 0,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'inside',
          formatter: (params: any) => {
            return `${Math.round(params.percent)}%`;
          },
          color: '#ffffff',
          fontSize: isSmallScreen ? 12 : 14,
          fontWeight: 'bold',
          textBorderColor: '#001E50',
          textBorderWidth: 2,
        },
        emphasis: {
          label: {
            show: false,
            fontSize: isSmallScreen ? 14 : 16,
            fontWeight: 'bold',
            color: '#ffffff'
          }
        },
        labelLine: {
          show: false
        },
        data: [
          {
            value: trabalhados,
            name: 'Leads Trabalhados',
            itemStyle: { color: '#001E50' }
          },
          {
            value: pendentes,
            name: 'Leads Pendentes',
            itemStyle: { color: '#57737d' }
          }
        ]
      }
    ]
  };
}
}
